'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { getMicrocopyValue } from '@lib/util/microcopy';
import { useMemo } from 'react';
import { Dropdown, DropdownOption } from 'ui-components';
import { getPageSizeOptions } from '../utils/pageSize';
import { ResultsPerPageControlProps } from './ResultsPerPage.types';

/**
 * Presentational page-size dropdown shared by the search and publisher-profile
 * surfaces — each supplies its own engine-backed state and selection handler,
 * so any styling, microcopy, or a11y change lands on both at once.
 */
export const ResultsPerPageControl = ({
  numberOfResults,
  onSelect,
  className = '',
  disabled = false,
}: ResultsPerPageControlProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
  }>();

  const resultsLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.results-per-page',
    'Results'
  );

  const options: DropdownOption[] = useMemo(
    () =>
      getPageSizeOptions(numberOfResults).map((size) => ({
        label: String(size),
        value: String(size),
      })),
    [numberOfResults]
  );

  const baseOption =
    options.find((opt) => opt.value === String(numberOfResults)) ?? options[0];

  const selectedOption: DropdownOption = useMemo(
    () => ({
      ...baseOption,
      label: (
        <>
          <span className="text-content-subtle">{resultsLabel}:</span>{' '}
          <span className="font-normal text-content opacity-90">
            {baseOption.label}
          </span>
        </>
      ),
    }),
    [baseOption, resultsLabel]
  );

  return (
    <div className={className}>
      <Dropdown
        id="results-per-page"
        options={options}
        selectedOption={selectedOption}
        onSelect={(option) => onSelect(parseInt(option.value, 10))}
        isDarkTheme={false}
        buttonClassName="bg-transparent"
        ariaLabel={getMicrocopyValue(
          searchLabelsMicrocopy,
          'search.results-per-page-aria-label',
          `Show ${baseOption.label} results per page`
        )}
        className="[&_[role=option]]:m-0.5 [&_[role=option]]:p-1.5"
        disabled={disabled}
      />
    </div>
  );
};
